
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Play, Clock, BookOpen } from 'lucide-react';
import { useUserProgress } from '@/hooks/useUserProgress';
import { useAuth } from '@/hooks/useAuth';

interface ProgressIndicatorProps {
  nodeId: string;
  variant?: 'badge' | 'bar' | 'icon';
  showTimeSpent?: boolean;
  className?: string;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ 
  nodeId, 
  variant = 'badge',
  showTimeSpent = false,
  className = ''
}) => {
  const { user } = useAuth();
  const { data: progressData } = useUserProgress(nodeId);

  if (!user) {
    return null; // Don't show progress for non-authenticated users
  }

  const currentProgress = progressData?.find(p => p.node_id === nodeId);
  const status = currentProgress?.status || 'not_started';
  const progressPercentage = currentProgress?.progress_percentage || 0;
  const timeSpent = currentProgress?.time_spent_seconds || 0;

  const getStatusConfig = () => {
    switch (status) {
      case 'completed':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bgColor: 'bg-white0',
          label: 'Completed',
          variant: 'default' as const
        };
      case 'in_progress':
        return {
          icon: Play,
          color: 'text-blue-500',
          bgColor: 'bg-white0',
          label: 'In Progress',
          variant: 'secondary' as const
        };
      case 'viewed':
        return {
          icon: Clock,
          color: 'text-sky-500',
          bgColor: 'bg-white0',
          label: 'Viewed',
          variant: 'outline' as const
        };
      default:
        return {
          icon: BookOpen,
          color: 'text-gray-400',
          bgColor: 'bg-gray-400',
          label: 'Not Started',
          variant: 'outline' as const
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  if (variant === 'icon') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Icon className={`h-4 w-4 ${config.color}`} />
        {showTimeSpent && timeSpent > 0 && (
          <span className="text-xs text-black">
            {Math.floor(timeSpent / 60)}m
          </span>
        )}
      </div>
    );
  }

  if (variant === 'bar') {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${config.color}`} />
            <span>{config.label}</span>
          </div>
          {showTimeSpent && timeSpent > 0 && (
            <span className="text-black">
              {Math.floor(timeSpent / 60)} min
            </span>
          )}
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </div>
    );
  }

  // Default badge variant
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
      {showTimeSpent && timeSpent > 0 && (
        <span className="text-xs text-black">
          {Math.floor(timeSpent / 60)}m
        </span>
      )}
    </div>
  );
};

export default ProgressIndicator;
